import React, { useState } from 'react';

const ImportCSVDataTab = ({ table }) => {
  const [csvText, setCsvText] = useState('');
  const [hasHeader, setHasHeader] = useState(true);
  const [onDuplicateUpdate, setOnDuplicateUpdate] = useState(false);
  const [generatedSql, setGeneratedSql] = useState('');
  const [copied, setCopied] = useState(false);

  // Helper to parse individual CSV lines considering quoted fields
  const parseCSVLine = (line) => {
    const values = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"' && line[i + 1] === '"') {
        current += '"';
        i++;
      } else if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        values.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    values.push(current.trim());
    return values;
  };

  const escapeSqlValue = (val) => {
    if (val === null || val === undefined || val === '' || val.toUpperCase() === 'NULL') {
      return 'NULL';
    }
    return `'${val.replace(/'/g, "''")}'`;
  };

  const generateInsertQuery = () => {
    if (!csvText.trim()) return;

    const lines = csvText
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter((line) => line.length > 0);

    if (lines.length === 0) return;

    const tableName = table || 'table_name';
    let rawHeaders = [];
    let columns = [];
    let startIndex = 0;

    if (hasHeader) {
      rawHeaders = parseCSVLine(lines[0]).map((col) => col.replace(/`/g, '').trim());
      columns = rawHeaders.map((col) => `\`${col}\``);
      startIndex = 1;
    }

    const valueTuples = [];

    for (let i = startIndex; i < lines.length; i++) {
      const rawValues = parseCSVLine(lines[i]);
      const formattedValues = rawValues.map(escapeSqlValue);
      valueTuples.push(`(${formattedValues.join(', ')})`);
    }

    if (valueTuples.length === 0) return;

    const columnClause = columns.length > 0 ? ` (${columns.join(', ')})` : '';
    let sql = `INSERT INTO \`${tableName}\`${columnClause} VALUES\n` + valueTuples.join(',\n');

    // Add ON DUPLICATE KEY UPDATE for all columns
    if (onDuplicateUpdate && rawHeaders.length > 0) {
      const updateClause = rawHeaders
        .map((col) => `\`${col}\` = VALUES(\`${col}\`)`)
        .join(',\n  ');
      sql += `\nON DUPLICATE KEY UPDATE\n  ${updateClause}`;
    }

    sql += ';';

    setGeneratedSql(sql);
    setCopied(false);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(generatedSql);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleClear = () => {
    setCsvText('');
    setGeneratedSql('');
  };

  return (
    <div className="pma-import-container p-2">
      <div className="card mb-3">
        <div className="card-header bg-light fw-bold" style={{ fontSize: '13px' }}>
          Import Raw CSV Data to Table: <span className="text-primary">{table || 'table_name'}</span>
        </div>
        <div className="card-body">
          {/* Options */}
          <div className="mb-3 d-flex gap-4">
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id="hasHeaderCheck"
                checked={hasHeader}
                onChange={(e) => setHasHeader(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="hasHeaderCheck" style={{ fontSize: '12px' }}>
                First row contains column headers
              </label>
            </div>

            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id="onDuplicateCheck"
                checked={onDuplicateUpdate}
                disabled={!hasHeader}
                onChange={(e) => setOnDuplicateUpdate(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="onDuplicateCheck" style={{ fontSize: '12px' }}>
                Add ON DUPLICATE KEY UPDATE for all columns
              </label>
            </div>
          </div>

          {/* Raw CSV Textarea */}
          <div className="mb-3">
            <label className="form-label fw-semibold" style={{ fontSize: '12px' }}>
              Paste CSV Content
            </label>
            <textarea
              className="form-control font-monospace"
              rows="8"
              style={{ fontSize: '12px' }}
              placeholder={`id,name,email\n1,"John Doe",john@example.com\n2,"Jane Smith",jane@example.com`}
              value={csvText}
              onChange={(e) => setCsvText(e.target.value)}
            />
          </div>

          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={generateInsertQuery}
              disabled={!csvText.trim()}
            >
              Generate INSERT Query
            </button>
            <button
              type="button"
              className="btn btn-sm btn-secondary"
              onClick={handleClear}
              disabled={!csvText.trim() && !generatedSql}
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      {/* Generated SQL Output */}
      {generatedSql && (
        <div className="card">
          <div className="card-header bg-light d-flex justify-content-between align-items-center" style={{ fontSize: '13px' }}>
            <span className="fw-bold">Generated SQL</span>
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={handleCopy}>
              {copied ? 'Copied!' : 'Copy SQL'}
            </button>
          </div>
          <div className="card-body p-0">
            <textarea
              className="form-control font-monospace rounded-0 border-0 p-3"
              rows="10"
              style={{ fontSize: '12px', backgroundColor: '#f8f9fa' }}
              readOnly
              value={generatedSql}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ImportCSVDataTab;